// Réglages des inscriptions (page Réglages) : ouverture du formulaire, serveur d'envoi des e-mails, liste noire.
import { useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import { t } from "../lib/i18n";
import { Button, Checkbox, Field, FormError, Panel, Pill, Select, TextInput, useToast } from "./ui";

type View = components["schemas"]["SignupSettingsView"];
type Mailer = components["schemas"]["MailerIn"];

export function SignupPanel() {
  const toast = useToast();
  const [view, setView] = useState<View | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [notifyTo, setNotifyTo] = useState("");
  const [blocklist, setBlocklist] = useState("");
  const [mailer, setMailer] = useState<Mailer>({ smtp_host: "", smtp_port: 587, smtp_security: "starttls", smtp_user: "", smtp_password: "", mail_from: "" });
  const [port, setPort] = useState("587");          // texte tel que saisi : le champ peut être vidé puis retapé
  const [testTo, setTestTo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const show = (v: View) => {
    setView(v);
    setEnabled(v.enabled);
    setPublicUrl(v.public_url || window.location.origin);
    setNotifyTo(v.notify_to);
    setBlocklist(v.blocklist.join("\n"));
    setMailer({ ...v.mailer, smtp_password: "" });
    setPort(String(v.mailer.smtp_port));
  };
  useEffect(() => {
    unwrap(api.GET("/api/signup/settings")).then(show).catch((e: Error) => setError(e.message));
  }, []);
  const m = <K extends keyof Mailer>(k: K, v: Mailer[K]) => setMailer((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      show(await unwrap(api.PUT("/api/signup/settings", { body: {
        enabled, public_url: publicUrl.trim(), notify_to: notifyTo.trim(),
        blocklist: blocklist.split(/[\s,;]+/).filter(Boolean),
        mailer: { ...mailer, smtp_port: Number(port) || 587, smtp_password: mailer.smtp_password || null },
      } })));
      toast(enabled ? t("Inscriptions ouvertes") : t("Réglages des inscriptions enregistrés"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    try {
      const r = await unwrap(api.POST("/api/signup/settings/test", { body: { to: testTo.trim() } }));
      toast(r.ok ? t("E-mail de test envoyé à {to}", { to: testTo }) : t("Échec : {error}", { error: r.error ?? "" }), r.ok ? "ok" : "err");
    } catch (e) {
      toast((e as Error).message, "err");
    }
  };

  if (!view) return <Panel title={t("Inscriptions")}><p className="p-4 text-muted">{error ?? t("Chargement…")}</p></Panel>;
  return (
    <Panel title={t("Inscriptions")}>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {view.enabled ? <Pill tone="ok">{t("Formulaire ouvert")}</Pill> : <Pill tone="muted">{t("Formulaire fermé")}</Pill>}
          <span className="text-[13px] text-muted">
            {t("Les demandes confirmées arrivent dans la page Demandes. Seule l'adresse qui a reçu le lien peut créer le compte.")}
          </span>
        </div>
        <Checkbox id="su-enabled" checked={enabled} onChange={setEnabled}
          label={t("Ouvrir le formulaire « Demander un accès » sur la page de connexion")} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Adresse de l'interface")} htmlFor="su-url" hint={t("Utilisée dans les liens des e-mails. En HTTPS sur Internet.")}>
            <TextInput id="su-url" value={publicUrl} maxLength={200} onChange={(e) => setPublicUrl(e.target.value)}
              placeholder={t("https://gateway.exemple.com")} className="font-mono text-[13px]" />
          </Field>
          <Field label={t("Prévenir de chaque nouvelle demande (facultatif)")} htmlFor="su-notify">
            <TextInput id="su-notify" type="email" value={notifyTo} maxLength={254} onChange={(e) => setNotifyTo(e.target.value)}
              placeholder={t("vous@exemple.com")} />
            {view.notify_to && <p className="mt-1 text-[12px] text-faint">
              {view.notify_lang === "en" ? t("Alerte envoyée en anglais (langue de votre compte).")
                : t("Alerte envoyée en français (langue de votre compte).")}
            </p>}
          </Field>
        </div>
        <div className="rounded-md border border-line bg-raised/40 p-3">
          <div className="mb-3 font-cond text-[13px] font-semibold uppercase tracking-wider text-faint">{t("Envoi des e-mails (SMTP)")}</div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("Serveur")} htmlFor="su-host">
              <TextInput id="su-host" value={mailer.smtp_host} onChange={(e) => m("smtp_host", e.target.value)} placeholder={t("smtp.exemple.com")} />
            </Field>
            <Field label={t("Port")} htmlFor="su-port">
              <TextInput id="su-port" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))} />
            </Field>
            <Field label={t("Sécurité")} htmlFor="su-sec">
              <Select id="su-sec" value={mailer.smtp_security} onChange={(e) => m("smtp_security", e.target.value as Mailer["smtp_security"])}>
                <option value="starttls">{t("STARTTLS (port 587)")}</option>
                <option value="ssl">{t("SSL (port 465)")}</option>
                <option value="none">{t("Aucune")}</option>
              </Select>
            </Field>
            <Field label={t("Utilisateur")} htmlFor="su-user">
              <TextInput id="su-user" value={mailer.smtp_user} autoComplete="off" onChange={(e) => m("smtp_user", e.target.value)} />
            </Field>
            <Field label={t("Mot de passe")} htmlFor="su-pass" hint={view.mailer.has_password ? t("Enregistré : vide = inchangé.") : undefined}>
              <TextInput id="su-pass" type="password" autoComplete="new-password" value={mailer.smtp_password ?? ""}
                onChange={(e) => m("smtp_password", e.target.value)} />
            </Field>
            <Field label={t("Expéditeur")} htmlFor="su-from">
              <TextInput id="su-from" value={mailer.mail_from} onChange={(e) => m("mail_from", e.target.value)} placeholder={t("gateway@exemple.com")} />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <Field label={t("Essayer l'envoi (après Enregistrer)")} htmlFor="su-test">
              <TextInput id="su-test" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={t("vous@exemple.com")} />
            </Field>
            <Button onClick={test} disabled={!testTo.trim()}>{t("Envoyer un e-mail de test")}</Button>
          </div>
        </div>
        <Field label={t("Liste noire")} htmlFor="su-block"
          hint={t("Une adresse (nom@exemple.com) ou un domaine entier (exemple.com) par ligne : ne peuvent plus s'inscrire. Le compte d'une adresse ajoutée ici est désactivé aussitôt.")}>
          <textarea id="su-block" rows={3} value={blocklist} onChange={(e) => setBlocklist(e.target.value)}
            className="w-full rounded-md border border-line bg-bg px-3 py-2 font-mono text-[13px] text-ink outline-none focus:border-accent" />
        </Field>
        <FormError message={error} />
        <div><Button variant="primary" onClick={save} disabled={busy}>{t("Enregistrer")}</Button></div>
      </div>
    </Panel>
  );
}
